import NiceModal from "@ebay/nice-modal-react";
import type { ModalTaskType } from "./modalOrchestratorSlice";

// ---- 懒加载弹窗组件（避免循环依赖）----
import StopService from "@/pages/stopService";
import DownloadGateModal from "@/features/install/downloadGateModal/DownloadGateModal";
import LoginRemindPopupModal from "@/features/register/loginRemindPopup/LoginRemindPopupModal";
import LoginPopup from "@/pages/LoginPopup";
import InstallAfterAuthPromptModal from "@/features/install/installAfterAuthPrompt/InstallAfterAuthPromptModal";
import FirstUpPopupModal from "@/features/firstUp/firstUpPopup/FirstUpPopupModal";
import ActivityPopup from "@/features/activity/activityPopup/ActivityPopup";
import InvitePopupModal from "@/features/invite/invitePopup/InvitePopupModal";
import InviteWheelEntryModal from "./InviteWheelEntryModal";
import { hasPendingInviteWheelEntry } from "./inviteWheelEntry";

// ---- 执行上下文 ----

export interface ExecutorContext {
  /** 移动端 iframe 游戏是否打开中 */
  isMobileGameIframeOpen: boolean;
  /** DialogPlus 对话框是否打开中（钱包、VIP、分享等） */
  isDialogPlusOpen: boolean;
  /** 用户是否在首页 */
  isOnHomePage: boolean;
  isWalletOpen: boolean;
  isPageVisible: boolean;
  isOtherPanelOpen: boolean;
}

/**
 * 普通公告、安装提示和营销的展示门控。
 * 注意：此函数仅由排水循环调用，用于决定是否 defer 任务。
 * executor 内不再做门控，避免任务被静默完成。
 * @returns true = 通过检查可以展示
 */
export const passesPopupGating = (ctx: ExecutorContext): boolean => {
  if (!ctx.isPageVisible || ctx.isWalletOpen || ctx.isOtherPanelOpen)
    return false;
  if (ctx.isMobileGameIframeOpen) {
    // eslint-disable-next-line no-console
    console.log("[orchestrator] gating: mobile game iframe open, defer");
    return false;
  }
  if (ctx.isDialogPlusOpen) {
    // eslint-disable-next-line no-console
    console.log("[orchestrator] gating: dialog plus open, defer");
    return false;
  }
  if (!ctx.isOnHomePage) {
    // eslint-disable-next-line no-console
    console.log("[orchestrator] gating: not on home page, defer");
    return false;
  }
  return true;
};

// ---- 任务执行器映射 ----

type TaskExecutor = (
  payload: Record<string, any>,
  ctx: ExecutorContext,
  signal: AbortSignal
) => Promise<void>;

/** NiceModal.remove 本身不会结束 show 的 Promise；取消时显式结束执行器等待。 */
const showModal = (modal: any, args: any, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Popup task cancelled", "AbortError"));
      return;
    }
    const onAbort = () => {
      NiceModal.remove(modal);
      reject(new DOMException("Popup task cancelled", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
    try {
      const pending =
        args === undefined
          ? NiceModal.show(modal)
          : NiceModal.show(modal, args);
      pending.then(
        () => {
          signal.removeEventListener("abort", onAbort);
          resolve();
        },
        (error) => {
          signal.removeEventListener("abort", onAbort);
          reject(error);
        }
      );
    } catch (error) {
      signal.removeEventListener("abort", onAbort);
      reject(error);
    }
  });

export const taskExecutors: Record<ModalTaskType, TaskExecutor> = {
  stop_service: (_payload, _ctx, signal) =>
    showModal(StopService, undefined, signal),
  download_gate: (payload, _ctx, signal) =>
    showModal(DownloadGateModal, payload, signal),
  login_remind: (_payload, _ctx, signal) =>
    showModal(LoginRemindPopupModal, undefined, signal),
  invite_wheel_entry: (_payload, _ctx, signal) =>
    hasPendingInviteWheelEntry()
      ? showModal(InviteWheelEntryModal, { signal }, signal)
      : Promise.resolve(),
  login_pop_queue: async (payload, _ctx, signal) => {
    for (const item of payload.items || []) {
      if (signal.aborted) return;
      if (item) await showModal(LoginPopup, item, signal);
    }
  },
  install_prompt: (payload, _ctx, signal) =>
    showModal(InstallAfterAuthPromptModal, { env: payload.env }, signal),
  first_deposit: (_payload, _ctx, signal) =>
    showModal(FirstUpPopupModal, undefined, signal),
  activity_popup: async (payload, _ctx, signal) => {
    if (payload.popList?.length) {
      await showModal(ActivityPopup, { popList: payload.popList }, signal);
    }
  },
  invite_popup: (_payload, _ctx, signal) =>
    showModal(InvitePopupModal, undefined, signal),
};
